import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shield, Landmark, FileSignature, History, Eye, ArrowRight } from "lucide-react";

const API = "/api"; // same-origin — the ingress proxies /api to the backend
const GOLD = "#eec643";
const card = "rounded-xl border border-stone-700 bg-stone-900/60 p-5";
const label = "text-[10px] font-mono uppercase tracking-widest text-amber-500/80 mb-3";
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);

const STATUS = {
  LINKED: ["LINKED", "Both parents hold active wallets — the household rail is running."],
  INVITE_PENDING: ["INVITED", "The co-parent's YES is outstanding — nothing has linked and nothing has moved."],
  UNLINKED: ["NOT YET LINKED", "No covenant exists yet — this window shows the structure waiting for her answer."],
};

export default function CovenantPeek() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/covenant/peek/${token}`);
        const body = await res.json();
        if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "This window link is not active.");
        setData(body);
      } catch (e) { setError(e.message); }
    })();
  }, [token]);

  if (error) {
    return (
      <div data-testid="peek-page" className="min-h-screen bg-stone-950 text-stone-200 flex items-center justify-center px-6">
        <div className={`${card} max-w-md w-full text-center`}>
          <p data-testid="peek-error" className="text-sm text-stone-300 mb-4">{error}</p>
          <p className="text-xs text-stone-500 mb-5">Window links rotate and revoke at the host's hand. Ask your co-parent for the current one.</p>
          <Link to="/story" data-testid="peek-story-link" className="text-amber-400 text-xs uppercase tracking-[0.3em] hover:text-amber-300 transition-colors">
            watch the story <ArrowRight size={12} className="inline -mt-0.5" />
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="peek-page" className="min-h-screen bg-stone-950 text-stone-500 flex items-center justify-center font-mono text-xs tracking-[0.3em]">
        OPENING THE WINDOW…
      </div>
    );
  }

  const [statusTag, statusLine] = STATUS[data.status] || STATUS.UNLINKED;
  const cov = data.covenant;

  return (
    <div data-testid="peek-page" className="min-h-screen bg-stone-950 text-stone-200">
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500">
            <Eye size={12} className="inline mr-2 -mt-0.5" />a window into the family covenant — read-only
          </p>
          <span data-testid="peek-status" className="font-mono text-[10px] text-amber-300 border border-amber-500/40 rounded-full px-3 py-1">{statusTag}</span>
        </div>

        <div className={card}>
          <p className={label}><Shield size={12} className="inline mr-2 -mt-0.5" />what you are looking at</p>
          <p data-testid="peek-host" className="text-sm text-stone-300 leading-relaxed mb-3">
            <span className="text-stone-100 font-semibold">{data.host_label}</span> has opened this read-only
            window so you — and your attorneys — can see exactly what the Family Covenant is, at your own pace.
            This comes teammate to teammate: nothing here argues against you; it shows you what was built
            for your child and invites your side to examine it.
          </p>
          <p className="text-xs text-stone-500 leading-relaxed">
            {statusLine} The covenant is voluntary: a shared household rail where maintenance moves
            parent-to-parent, the child's pool releases only on both signatures, and every event seals
            into a record that cannot be re-ordered or quietly edited. Nevada law prefers that fit
            parents set the terms of their own custody by agreement (NRS 125C.0025; Troxel v. Granville)
            — this covenant is that agreement, offered for adoption. It takes no liberty from either
            parent and converts no right into a permission; it is a way to run the household in the open.
          </p>
        </div>

        {cov && (
          <>
            <div className={card}>
              <p className={label}><Eye size={12} className="inline mr-2 -mt-0.5" />mutual assent</p>
              <p data-testid="peek-assent-badge" className={`font-mono text-[11px] inline-block border rounded-full px-3 py-1 ${data.assent?.badge === "MUTUAL_ASSENT" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>
                {data.assent?.label || "NO SHARED AGREEMENT SEALED YET"}
              </p>
              {data.assent?.signature_blocks?.length > 0 && (
                <p className="font-mono text-[10px] text-stone-500 mt-2">on-chain signatures at blocks {data.assent.signature_blocks.join(" · ")}</p>
              )}
              {data.assent?.seal && (
                <p className="font-mono text-[10px] text-stone-600 mt-1 break-all">document seal · sha256 {data.assent.seal}</p>
              )}
              <p className="text-[10px] text-stone-500 mt-2 leading-relaxed">
                When both parents sign, the sealed text is the parents' own agreement — teammate to
                teammate, verifiable like every seal on this page, and offered for adoption by stipulation.
              </p>
            </div>
            <div className={card}>
              <p className={label}><Shield size={12} className="inline mr-2 -mt-0.5" />the covenant</p>
              <div data-testid="peek-covenant-facts" className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-stone-400">
                <span>with <span className="text-stone-200">{cov.partner_label || "co-parent"}</span></span>
                <span>split {cov.allocation_split_pct}/{100 - cov.allocation_split_pct}</span>
                <span>{cov.routing_locked ? "ROUTING LOCKED" : "routing open"}</span>
                <span>escrow {cov.escrow_deployed ? "deployed · 2-of-2" : "not yet deployed"}</span>
              </div>
              {Object.keys(data.milestones || {}).length > 0 && (
                <div data-testid="peek-milestones" className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(data.milestones).map(([catName, amt]) => (
                    <span key={catName} className="font-mono text-[10px] text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1">
                      {catName} · {fmt(amt)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className={card}>
              <p className={label}><Landmark size={12} className="inline mr-2 -mt-0.5" />the child's pool — real deposits only</p>
              <p data-testid="peek-pool-balance" className="font-mono text-3xl text-emerald-400 mb-3">{fmt(data.pool?.available_equity_usd)}</p>
              {(data.pool?.funding_history || []).map((h, i) => (
                <div key={i} data-testid="peek-funding-row" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                  <span className="text-emerald-400">+{fmt(h.amount_usd)} from {h.source}</span>
                  <span className="text-stone-500">block {h.block}</span>
                </div>
              ))}
              <p className="text-[10px] text-stone-500 mt-2">The balance is read live from the ledger — it rises only on real deposits and moves out only when both parents sign.</p>
            </div>

            {(data.proposals || []).length > 0 && (
              <div className={card}>
                <p className={label}><FileSignature size={12} className="inline mr-2 -mt-0.5" />spending proposals — nothing releases without both parents</p>
                {data.proposals.map((p) => (
                  <div key={p.proposal_id} data-testid={`peek-proposal-${p.proposal_id}`} className="flex items-center gap-2 flex-wrap font-mono text-[11px] py-2 border-b border-stone-800/60">
                    <span className="text-stone-500">#{p.proposal_id}</span>
                    <span className="text-stone-200 flex-1 min-w-[140px]">{p.purpose}</span>
                    <span className="text-[10px] text-stone-500 border border-stone-700 rounded-full px-2 py-0.5">{p.category}</span>
                    <span className="text-emerald-300">{fmt(p.amount_usd)}</span>
                    <span className={`text-[10px] ${p.status === "EXECUTED" ? "text-emerald-400" : "text-amber-400"}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}

            {(data.timeline || []).length > 0 && (
              <div className={card}>
                <p className={label}><History size={12} className="inline mr-2 -mt-0.5" />the household story so far</p>
                <div data-testid="peek-timeline">
                  {data.timeline.slice(0, 20).map((e, i) => (
                    <div key={i} data-testid="peek-timeline-event" className="flex gap-3 items-baseline py-1.5 border-l border-stone-700 pl-4 font-mono text-[11px]">
                      <span className="text-stone-500 whitespace-nowrap">{(e.at || "").slice(0, 10)}</span>
                      <span className="text-amber-500/80">{e.kind}</span>
                      <span className="text-stone-300">{e.text}</span>
                      {e.block != null && <span className="text-stone-600">· block {e.block}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className={card}>
          <p className={label}><History size={12} className="inline mr-2 -mt-0.5" />the sealed record</p>
          <p className="text-xs text-stone-400 leading-relaxed mb-2">
            <span data-testid="peek-ledger-count" className="text-stone-100 font-semibold">{data.ledger.sealed_entries}</span>{" "}
            {data.ledger.sealed_entries === 1 ? "entry" : "entries"} sealed into the conduct ledger — each one
            locked against the one before it, so the history cannot be rewritten.
          </p>
          {data.ledger.chain_head && (
            <p data-testid="peek-chain-head" className="font-mono text-[10px] text-stone-600 break-all">
              chain head · sha256 {data.ledger.chain_head}
            </p>
          )}
        </div>

        <div className={card}>
          <p className={label}>your move</p>
          <p className="text-sm text-stone-300 leading-relaxed mb-4">
            If you got the invite text, your side links with one reply — <span className="text-stone-100 font-semibold">YES</span>.
            Take whatever time you need; your attorneys can verify every seal on this page independently,
            and a printed presentment for counsel is available from the host.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <span data-testid="peek-join-cta" className="px-5 py-2.5 rounded-full font-semibold text-sm text-stone-950" style={{ background: GOLD }}>
              REPLY YES TO YOUR INVITE TEXT
            </span>
            <Link to="/story" data-testid="peek-story-link" className="text-amber-400 text-xs uppercase tracking-[0.3em] hover:text-amber-300 transition-colors">
              watch the story <ArrowRight size={12} className="inline -mt-0.5" />
            </Link>
          </div>
        </div>

        <p className="text-[9px] text-stone-600 leading-relaxed">
          Read-only window rendered {new Date(data.generated_at).toLocaleString("en-US")} UTC — structure and integrity
          only: no account identifiers, card data, or wallet internals appear here. The host can rotate or revoke
          this link at any time. The Family Covenant is voluntary recordkeeping; not legal advice; no legal outcome asserted.
        </p>
      </main>
    </div>
  );
}
